"use client";

import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useHandleImportClick,
  type HandleImportClickChainResult,
} from "@hooks/useHandleImportClick";
import { useResponsiveChatOpen } from "@hooks/useResponsiveChatOpen";
import { openPlaidConnectFlowModal } from "@components/PlaidConnectFlow/openPlaidConnectFlowModal";
import type { PlaidUploadFrom } from "@components/PlaidConnectFlow/types";
import { invalidateHasHoldings } from "../api/useHasHoldings";
import { openOverlapChatAfterConnect } from "../utils/openOverlapChatAfterConnect";

export interface ConnectBrokerOptions {
  /** `plaid_link_*` 四条事件里的 `entry`（`overlap_prompt` / `overlap_guide` / `overlap_request_card` …） */
  trackingEntry: string;
  returnToBrief?: boolean;
}

/**
 * 重叠分析各入口共用的「连券商 / 传截图」两个动作（SC-03 引导页、SC-04 引导弹窗、Chat 里的连账户卡）。
 * 只差 `trackingEntry` 与上传的 `from`，其余口径一致：不出付费墙、直达投资平台列表、
 * 绑定成功不弹 AccountReadPending（由同步状态卡承接）。
 */
export const useOverlapConnectFlows = () => {
  const queryClient = useQueryClient();
  const { handleImportClick } = useHandleImportClick();
  const { openResponsiveChat } = useResponsiveChatOpen();

  /** 返回 Plaid 链的结果，调用方据此决定是否上报「回到发起页」。成功时已刷新持仓并开 Chat。 */
  const connectBroker = useCallback(
    async ({
      trackingEntry,
      returnToBrief,
    }: ConnectBrokerOptions): Promise<HandleImportClickChainResult> => {
      const result = await handleImportClick({
        accountType: "invest",
        skipPaywall: true,
        suppressAccountReadPending: true,
        plaidTrackingContext: { entry: trackingEntry },
        onBindSuccess: () => invalidateHasHoldings(queryClient),
      });
      if (result.action === "success" || result.action === "confirm") {
        invalidateHasHoldings(queryClient);
        await openOverlapChatAfterConnect(openResponsiveChat, {
          returnToBrief,
        });
      }
      return result;
    },
    [handleImportClick, openResponsiveChat, queryClient],
  );

  /** 上传成功的协议行 / 附件由 PlaidConnectFlowModal 的 `entry="overlap"` 分支发出；未提交就关闭则什么都不发生 */
  const openUpload = useCallback(
    async (from: PlaidUploadFrom): Promise<void> => {
      await openPlaidConnectFlowModal({
        startManualUpload: true,
        entry: "overlap",
        from,
        suppressAccountReadPending: true,
      });
      invalidateHasHoldings(queryClient);
    },
    [queryClient],
  );

  return { connectBroker, openUpload };
};
